import { useState, useEffect, useCallback } from 'react';
import { getApiClient } from '../api/client';

// ─── Enum types ───────────────────────────────────────────────────────────────
export type ShopPlacementType = 'floor' | 'wall' | 'face_accessory' | 'hand' | 'foot';
export type DefaultCaptureMode = 'quick_capture' | 'multi_angle_capture';
export type DefaultQualityMode = 'fast_render' | 'studio_render';
export type DimensionUnit = 'cm' | 'mm' | 'in';
export type DimensionStrategy = 'manual_optional' | 'manual_required' | 'assume_1m';
export type ScaleAxisPriority = 'height_first' | 'width_first';
export type ImagePickPolicy = 'merchant_select' | 'first_only' | 'first_3';
export type PublishingMode = 'shopify_media' | 'external_r2' | 'hybrid';
export type ViewerMode = 'native_shopify' | 'model_viewer';
export type DeviceFilterMode = 'auto' | 'always_show' | 'hide_if_unsupported';
export type EnvironmentLighting = 'shopify_default' | 'studio_soft' | 'neutral_bright' | 'dramatic';

// ─── Settings interface ───────────────────────────────────────────────────────
export interface ShopSettings {
  default_placement_type: ShopPlacementType;
  default_capture_mode: DefaultCaptureMode;
  default_quality_mode: DefaultQualityMode;
  default_dimension_unit: DimensionUnit;
  default_dimension_strategy: DimensionStrategy;
  scale_axis_priority: ScaleAxisPriority;
  max_source_images: number;
  image_pick_policy: ImagePickPolicy;
  publishing_mode: PublishingMode;
  viewer_mode: ViewerMode;
  ar_button_label: string;
  enable_desktop_3d: boolean;
  ar_device_filter: DeviceFilterMode;
  default_environment_lighting: EnvironmentLighting;
  auto_generate_on_new_products: boolean;
  textured_output: boolean;
  admin_api_keys: string[];
  /** Send-only when saving; never returned from the server. */
  shopify_api_token?: string;
  /** Returned from server: true if a custom Shopify API token is stored. */
  shopify_api_token_set: boolean;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────
export const DEFAULT_SHOP_SETTINGS: ShopSettings = {
  default_placement_type: 'floor',
  default_capture_mode: 'quick_capture',
  default_quality_mode: 'fast_render',
  default_dimension_unit: 'cm',
  default_dimension_strategy: 'manual_optional',
  scale_axis_priority: 'height_first',
  max_source_images: 4,
  image_pick_policy: 'merchant_select',
  publishing_mode: 'hybrid',
  viewer_mode: 'model_viewer',
  ar_button_label: 'View in your space',
  enable_desktop_3d: true,
  ar_device_filter: 'auto',
  default_environment_lighting: 'shopify_default',
  auto_generate_on_new_products: false,
  textured_output: true,
  admin_api_keys: [],
  shopify_api_token: '',
  shopify_api_token_set: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getSettingsUrl(): string {
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get('shop');
  return shop ? `/api/settings?shop=${shop}` : '/api/settings';
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function normalizeSettings(raw: Record<string, unknown>): ShopSettings {
  const merged: ShopSettings = { ...DEFAULT_SHOP_SETTINGS };

  // Scalar string fields with allowed values — fall back to default if invalid
  const placementTypes: ShopPlacementType[] = ['floor', 'wall', 'face_accessory', 'hand', 'foot'];
  if (placementTypes.includes(raw.default_placement_type as ShopPlacementType)) {
    merged.default_placement_type = raw.default_placement_type as ShopPlacementType;
  }

  const captureModes: DefaultCaptureMode[] = ['quick_capture', 'multi_angle_capture'];
  if (captureModes.includes(raw.default_capture_mode as DefaultCaptureMode)) {
    merged.default_capture_mode = raw.default_capture_mode as DefaultCaptureMode;
  }

  const qualityModes: DefaultQualityMode[] = ['fast_render', 'studio_render'];
  if (qualityModes.includes(raw.default_quality_mode as DefaultQualityMode)) {
    merged.default_quality_mode = raw.default_quality_mode as DefaultQualityMode;
  }

  const dimUnits: DimensionUnit[] = ['cm', 'mm', 'in'];
  if (dimUnits.includes(raw.default_dimension_unit as DimensionUnit)) {
    merged.default_dimension_unit = raw.default_dimension_unit as DimensionUnit;
  }

  const dimStrategies: DimensionStrategy[] = ['manual_optional', 'manual_required', 'assume_1m'];
  if (dimStrategies.includes(raw.default_dimension_strategy as DimensionStrategy)) {
    merged.default_dimension_strategy = raw.default_dimension_strategy as DimensionStrategy;
  }

  const scaleAxes: ScaleAxisPriority[] = ['height_first', 'width_first'];
  if (scaleAxes.includes(raw.scale_axis_priority as ScaleAxisPriority)) {
    merged.scale_axis_priority = raw.scale_axis_priority as ScaleAxisPriority;
  }

  const pickPolicies: ImagePickPolicy[] = ['merchant_select', 'first_only', 'first_3'];
  if (pickPolicies.includes(raw.image_pick_policy as ImagePickPolicy)) {
    merged.image_pick_policy = raw.image_pick_policy as ImagePickPolicy;
  }

  const pubModes: PublishingMode[] = ['shopify_media', 'external_r2', 'hybrid'];
  if (pubModes.includes(raw.publishing_mode as PublishingMode)) {
    merged.publishing_mode = raw.publishing_mode as PublishingMode;
  }

  const viewerModes: ViewerMode[] = ['native_shopify', 'model_viewer'];
  if (viewerModes.includes(raw.viewer_mode as ViewerMode)) {
    merged.viewer_mode = raw.viewer_mode as ViewerMode;
  }

  const deviceFilters: DeviceFilterMode[] = ['auto', 'always_show', 'hide_if_unsupported'];
  if (deviceFilters.includes(raw.ar_device_filter as DeviceFilterMode)) {
    merged.ar_device_filter = raw.ar_device_filter as DeviceFilterMode;
  }

  const lightingPresets: EnvironmentLighting[] = ['shopify_default', 'studio_soft', 'neutral_bright', 'dramatic'];
  if (lightingPresets.includes(raw.default_environment_lighting as EnvironmentLighting)) {
    merged.default_environment_lighting = raw.default_environment_lighting as EnvironmentLighting;
  }

  // Numeric: clamp max_source_images to 1..6
  if (typeof raw.max_source_images === 'number') {
    merged.max_source_images = clamp(Math.round(raw.max_source_images), 1, 6);
  }

  // String: ar_button_label — trim, keep default if empty
  if (typeof raw.ar_button_label === 'string' && raw.ar_button_label.trim().length > 0) {
    merged.ar_button_label = raw.ar_button_label.trim().slice(0, 100);
  }

  // Booleans
  if (typeof raw.enable_desktop_3d === 'boolean') merged.enable_desktop_3d = raw.enable_desktop_3d;
  if (typeof raw.auto_generate_on_new_products === 'boolean') merged.auto_generate_on_new_products = raw.auto_generate_on_new_products;
  if (typeof raw.textured_output === 'boolean') merged.textured_output = raw.textured_output;

  // admin_api_keys: array of non-empty strings
  if (Array.isArray(raw.admin_api_keys)) {
    merged.admin_api_keys = (raw.admin_api_keys as unknown[])
      .filter((k): k is string => typeof k === 'string' && k.trim().length > 0)
      .map((k) => k.trim());
  }

  // shopify_api_token: never return the real token — always empty string
  merged.shopify_api_token = '';

  // shopify_api_token_set: boolean
  merged.shopify_api_token_set = !!raw.shopify_api_token_set;

  return merged;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useSettings() {
  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const client = getApiClient();
      const { data } = await client.get<Record<string, unknown>>(getSettingsUrl());
      setSettings(normalizeSettings(data));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSettings = useCallback(async (newSettings: Partial<ShopSettings>): Promise<ShopSettings> => {
    try {
      setSaving(true);
      setError(null);
      const client = getApiClient();
      const { data } = await client.post<Record<string, unknown>>(getSettingsUrl(), newSettings);
      const normalized = normalizeSettings(data);
      setSettings(normalized);
      return normalized;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save settings'));
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    error,
    saving,
    refetch: fetchSettings,
    saveSettings,
  };
}
